/**
 * What the signed-in person may do, read off the profile the shell already holds.
 *
 * The codenames are the backend's — `add__member`, `change__club` — and a
 * superuser passes every check, as they do there. The answer is only ever a
 * hint for what to show: the API refuses on its own, so a button hidden here
 * is a button that would have failed, not a rule enforced here.
 *
 * `useCan` answers false until the profile has loaded, so an action never
 * flashes into view for a person who cannot take it.
 */

import { useQuery } from "@tanstack/react-query";

import { profileQuery } from "@/services/queries";
import type { Profile } from "@/meta/types";

/** Any one of the codenames is enough — the same rule as `requires(..., require_all=False)`. */
export function can(profile: Profile | undefined, ...codenames: string[]): boolean {
  if (!profile) return false;
  if (profile.is_superuser) return true;
  return codenames.some((codename) => profile.permissions.includes(codename));
}

export function useCan(): (...codenames: string[]) => boolean {
  const { data: profile } = useQuery(profileQuery());
  return (...codenames) => can(profile, ...codenames);
}
